// src/Routers.jsx

import React, { Suspense, lazy, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, useLocation, Navigate } from 'react-router-dom';

// Store hooks
import useLayoutStore from '@/stores/layout/layoutStore';

// Layouts
import LayoutShell from '@/layouts/LayoutShell';
import LayoutLiveAgent from '@/layouts/LayoutLiveAgent';
import LayoutDemo from '@/layouts/LayoutDemo';
import LayoutPublic from '@/layouts/LayoutPublic';

// Route wrappers
import Entrypoint from '@/router/main/Entrypoint';
import PrivateRoute from '@/router/main/PrivateRoute';
import PublicRoute from '@/router/main/PublicRoute';
import DashboardRedirect from '@/router/main/DashboardRedirect';
import RootLayout from '@/layouts/RootLayout';

// Global loading & error
import DLoading from '@/components/DLoading';
import ErrorPage from '@/pages/Error';
import ChangePasswordPage from '@/pages/Auth/ChangePassword';
import PageViewTracker from '@/components/PageViewTracker';
import RouteChangeHandler from '@/components/RouteChangeHandler';
import LayoutWrapper from '@/layouts/LayoutWrapper';
import LayoutPublicNoPadding from '@/pages/SignUpCampaign/LayoutPublicNoPadding';
import OnboardingVersionLayout from '@/v3-dashboard/components/OnboardingVersionLayout';
import BucketeerTestPage from '@/pages/BucketeerTest';
const UpgradeModalGallery = lazy(() => import('@/pages/UpgradeModalGallery'));
import AddPersonality from '@/v3-dashboard/pages/AddPersonality';
import DesktopLoginRedirect from '@/router/main/DesktopLoginRedirect';
import Preview from '@/v3-dashboard/pages/Preview';
import TokenCheckRoute from '@/router/main/TokenCheckRoute';

// ============ Lazy-Loaded Pages ============
const BubblePreviewPage = lazy(() => import('@/pages/BubblePreviewPage'));

// Auth
const LogIn = lazy(() => import('@/pages/LogIn'));
const ForgotPasswordPage = lazy(() => import('@/pages/Auth/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('@/pages/Auth/ResetPassword'));
const SetPasswordPage = lazy(() => import('@/pages/Auth/SetPassword'));
const SigninExchangePage = lazy(() => import('@/pages/Auth/SigninExchange'));
const SignUp = lazy(() => import('@/pages/SignUp'));
const SignUpCampaign = lazy(() => import('@/pages/SignUpCampaign'));
const AcceptTeamInvite = lazy(() =>
  import('@/pages/TeamManagement/AcceptTeamInvite')
);

// Home & Misc
const Home = lazy(() => import('@/pages/Home'));
const FirstTimePage = lazy(() => import('@/pages/FirstTimePage'));
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const Upgrade = lazy(() => import('@/pages/Upgrade'));
const Success = lazy(() => import('@/pages/Stripe/Success'));
const Cancel = lazy(() => import('@/pages/Stripe/Cancel'));
const Subscribe = lazy(() => import('@/pages/Subscribe'));
const Plans = lazy(() => import('@/pages/Plans'));
const Membership = lazy(() => import('@/pages/Membership'));
const MembershipPage = lazy(() => import('@/pages/Membership'));
const Profile = lazy(() => import('@/pages/Profile'));
const WeeklyWebinar = lazy(() => import('@/pages/WeeklyWebinar'));
const TrainingPage = lazy(() => import('@/pages/Training'));

// Human Handover
const HumanHandover = lazy(() => import('@/v3-dashboard/pages/HumanHandover'));
const AddAgent = lazy(() => import('@/v3-dashboard/pages/HumanHandover/AddAgent'));
const EditAgent = lazy(() => import('@/v3-dashboard/pages/HumanHandover/EditAgent'));
const HumanHandoverInsights = lazy(() =>
  import('@/v3-dashboard/pages/HumanHandover/Insights')
);
const LiveAgentEntrypoint = lazy(() => import('./LiveAgentEntrypoint'));
const HumanHandoverQuickResponses = lazy(() =>
  import('@/v3-dashboard/pages/HumanHandover/QuickResponses')
);
const HumanHandoverSettings = lazy(() =>
  import('@/v3-dashboard/pages/HumanHandover/Settings')
);
const HumanHandoverConversation = lazy(() =>
  import('@/v3-dashboard/pages/HumanHandover/Conversation')
);
const HumanHandoverDashboard = lazy(() =>
  import('@/v3-dashboard/pages/HumanHandover/Dashboard')
);
const VerifyHumanHandoverEmail = lazy(() =>
  import('@/v3-dashboard/pages/HumanHandover/VerifyHumanHandoverEmail')
);

// Chatbot
const CreateChatbot = lazy(() => import('@/v3-dashboard/pages/CreateChatbot'));
const ChatbotDetails = lazy(() => import('@/pages/ChatbotDetails'));
const ChatbotDetailsOutlet = lazy(() =>
  import('@/pages/ChatbotDetails/ChatbotDetailsOutlet')
);
const ChatbotDashboard = lazy(() => import('@/v3-dashboard/pages/ChatbotDetails/ChatbotDashboard'));
const ChatbotTabs = lazy(() => import('@/pages/ChatbotDetails/ChatbotTabs'));
const ChatbotInsightsLayout = lazy(() =>
  import('@/pages/ChatbotDetails/ChatbotInsightsLayout')
);
const ChatbotInsightsTrends = lazy(() =>
  import('@/pages/ChatbotDetails/ChatbotInsightsTrends')
);
const ChatbotInsightsLeads = lazy(() =>
  import('@/pages/ChatbotDetails/ChatbotInsightsLeads')
);
const ChatAllRecords = lazy(() =>
  import('@/pages/ChatbotDetails/ChatAllRecords')
);
const ChatRecordDeatil = lazy(() =>
  import('@/pages/ChatbotDetails/ChatRecordDeatil/ChatRecordDeatil')
);
const ChatbotIntegrations = lazy(() =>
  import('@/pages/ChatbotDetails/ChatbotIntegrations')
);
const WhatsappIntegration = lazy(() =>
  import('@/v3-dashboard/pages/Integrations/WhatsappIntegration')
);
const WordPressIntegration = lazy(() =>
  import('@/v3-dashboard/pages/Integrations/WordpressIntegration')
);
const IntercomIntegration = lazy(() =>
  import('@/v3-dashboard/pages/Integrations/IntercomIntegration')
);
const ChatbotSafety = lazy(() =>
  import('@/pages/ChatbotDetails/ChatbotSafety')
);
const ChatbotKnowledge = lazy(() =>
  import('@/pages/ChatbotDetails/ChatbotKnowledge')
);
const ChatbotSetup = lazy(() => import('@/pages/ChatbotDetails/ChatbotSetup'));
const ChatbotStyling = lazy(() =>
  import('@/pages/ChatbotDetails/ChatbotStyling')
);
const ChatbotPowerups = lazy(() =>
  import('@/pages/ChatbotDetails/ChatbotPowerups')
);
const ChatbotRealtimeVoice = lazy(() =>
  import('@/pages/ChatbotDetails/ChatbotRealtimeVoice')
);
const ChatbotPersonality = lazy(() =>
  import('@/pages/ChatbotDetails/ChatbotPersonality')
);
const AddKnowledgeFile = lazy(() =>
  import('@/pages/ChatbotDetails/AddKnowledgeFile')
);
const ChatbotDeployments = lazy(() =>
  import('@/v3-dashboard/pages/ChatbotDeployments')
);
const ChatbotDeploymentAdd = lazy(() =>
  import('@/v3-dashboard/pages/ChatbotDeploymentAdd')
);
const ShareAIChatbot = lazy(() =>
  import('@/v3-dashboard/pages/ShareAIChatbot')
);

const Behavior = lazy(() =>
  import('@/v3-dashboard/pages/Behavior')
);

// Team
const TeamManagement = lazy(() => import('@/pages/TeamManagement'));

// Voice
const Voice = lazy(() => import('@/pages/Voice'));
const CreateVoice = lazy(() => import('@/pages/CreateVoice'));
const PhoneNumbers = lazy(() => import('@/pages/Voice/PhoneNumbers'));
const PhoneNumberPurchase = lazy(() => import('@/pages/Voice/PhoneNumbers/Purchase'));
const PhoneNumberPurchaseSuccess = lazy(() => import('@/pages/Voice/PhoneNumbers/Purchase/PurchaseSuccess'));
const PhoneNumberPurchaseCancel = lazy(() => import('@/pages/Voice/PhoneNumbers/Purchase/PurchaseCancel'));
const EditVoice = lazy(() => import('@/pages/EditVoice'));
const VoiceConversations = lazy(() => import('@/pages/Voice/Conversations'));
const VoiceConversationDetails = lazy(() => import('@/pages/Voice/ConversationDetail'));
const VoiceDetailsOutlet = lazy(() => import('@/pages/Voice/VoiceDetailsOutlet'));
const VoiceInsights = lazy(() => import('@/pages/Voice/Insights'));
const AIVoiceDemo = lazy(() => import('@/pages/AIVoiceDemo'));
// Demo
const Demo = lazy(() => import('@/pages/Demo'));
const ArabicDemo = lazy(() => import('@/pages/ArabicDemo'));

// Admin
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));

// New Onboarding - version (v1/v2) is selected inside OnboardingVersionLayout

// Animated page transition
import AnimatedPageTransition from '@/components/AnimatedPageTransition';

// Mixpanel Session Replay Manager
import MixpanelSessionReplayManager from '@/components/MixpanelSessionReplayManager';

// Account Status
const AccountPaused = lazy(() => import('@/v3-dashboard/pages/AccountPaused'));

// Main Dashboard
const MainDashboard = lazy(() => import('@/v3-dashboard/pages/MainDashboard'));
const Billing = lazy(() => import('@/v3-dashboard/pages/Billing'));
const BillingSubscription = lazy(() => import('@/v3-dashboard/pages/Billing/Subscription'));
const BillingAddOns = lazy(() => import('@/v3-dashboard/pages/Billing/AddOns'));
const BillingInvoices = lazy(() => import('@/v3-dashboard/pages/Billing/Invoices'));
const BillingPayment = lazy(() => import('@/v3-dashboard/pages/Billing/Payment'));

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // ================= Public Routes =================
      {
        path: '/log-in',
    element: (
      <PublicRoute>
        <LayoutPublicNoPadding leftSide={<LogIn />} />
      </PublicRoute>
    ),
  },
  {
    path: '/desktop-login',
    element: <DesktopLoginRedirect />,
  },
  {
    path: '/auth/exchange',
    element: <SigninExchangePage />,
  },
  {
    path: '/accept-organization-invite',
    element: (
      <LayoutPublicNoPadding leftSide={<VerifyHumanHandoverEmail />} />
    ),
  },
  {
    path: '/sign-up',
    element: (
      <PublicRoute>
        <LayoutPublicNoPadding leftSide={<SignUp />} />
      </PublicRoute>
    ),
  },
  {
    path: '/new-sign-up',
    element: (
      <PublicRoute>
        <LayoutPublicNoPadding leftSide={<SignUpCampaign />} />
      </PublicRoute>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <PublicRoute>
        <LayoutPublicNoPadding leftSide={<ForgotPasswordPage />} />
      </PublicRoute>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <PublicRoute>
        <LayoutPublicNoPadding leftSide={<ResetPasswordPage />} />
      </PublicRoute>
    ),
  },
  {
    path: '/set-password',
    element: (
      <PublicRoute>
        <LayoutPublicNoPadding leftSide={<SetPasswordPage />} />
      </PublicRoute>
    ),
  },
  {
    path: '/bkt-test',
    element: <BucketeerTestPage />,
  },
  {
    path: '/upgrade-modal-gallery',
    element: (
      <Suspense fallback={<DLoading />}>
        <UpgradeModalGallery />
      </Suspense>
    ),
  },
  {
    path: '/create-account/setting-up',
    element: (
      <AnimatedPageTransition direction="right">
        <OnboardingVersionLayout page="started" />
      </AnimatedPageTransition>
    ),
  },
  {
    path: '/create-account',
    element: (
      <PublicRoute>
        <AnimatedPageTransition direction="right">
          <OnboardingVersionLayout page="main" />
        </AnimatedPageTransition>
      </PublicRoute>
    ),
  },
  {
    path: '/create-account/verify',
    element: (
      <AnimatedPageTransition direction="right">
        <OnboardingVersionLayout page="verify" />
      </AnimatedPageTransition>
    ),
  },
  {
    path: '/create-account/claim',
    element: (
      <AnimatedPageTransition direction="right">
        <OnboardingVersionLayout page="claim" />
      </AnimatedPageTransition>
    ),
  },
  {
    path: '/create-account/no-website',
    element: (
      <AnimatedPageTransition direction="right">
        <OnboardingVersionLayout page="no-website" />
      </AnimatedPageTransition>
    ),
  },
  {
    path: '/account-paused',
    element: (
      <PrivateRoute>
        {/* <LayoutShell> */}
          <AccountPaused />
        {/* </LayoutShell> */}
      </PrivateRoute>
    ),
  },
  {
    path: '/request-token',
    element: (
      <PublicRoute>
        <LayoutPublicNoPadding leftSide={<div>RequestToken</div>} />
      </PublicRoute>
    ),
  },
  {
    path: '/verify-email',
    element: (
      <PublicRoute>
        <LayoutPublicNoPadding leftSide={<div>VerifyEmail</div>} />
      </PublicRoute>
    ),
  },
  {
    path: '/demo',
    element: (
      <>
        <LayoutDemo>
          <Demo />
        </LayoutDemo>
      </>
    ),
  },
  {
    path: '/weekly-webinar',
    element: (
      <>
        <PageViewTracker />
        <RouteChangeHandler />
        <WeeklyWebinar />
      </>
    ),
  },
  {
    path: '/dashboard',
    element: <DashboardRedirect />,
  },
  {
    path: '/',
    element: (
      <PrivateRoute>
        <Entrypoint />
      </PrivateRoute>
    ),
    errorElement: (
      <PrivateRoute>
        <ErrorPage />
      </PrivateRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <LayoutShell>
            <MainDashboard />
          </LayoutShell>
        ),
      },
      {
        path: 'first-time-setup',
        element: (
          <LayoutWrapper>
            <FirstTimePage />
          </LayoutWrapper>
        ),
      },
      {
        path: 'onboarding',
        element: (
          <PrivateRoute>
            <Outlet />
          </PrivateRoute>
        ),
        children: [
          { path: 'create', element: <Onboarding /> },
          { path: 'upgrade', element: <Navigate to="/" replace /> },
        ],
      },
      {
        path: 'chatbot',
        element: (
          <LayoutShell>
            <Outlet />
          </LayoutShell>
        ),
        children: [
          { path: 'create', element: <CreateChatbot /> },
          {
            path: ':id',
            element: <ChatbotDetailsOutlet />,
            children: [
              { index: true, element: <ChatbotDashboard />},
              { path: 'preview', element: <Preview /> },
              { path: 'dashboard', element: <ChatbotDashboard /> },
              { path: 'tabs', element: <ChatbotTabs /> },
              {
                path: 'insights',
                element: <ChatbotInsightsLayout />,
                children: [
                  { index: true, element: <ChatbotInsightsTrends /> },
                  { path: 'records', element: <ChatRecordDeatil /> },
                  { path: 'records/:recordId', element: <ChatRecordDeatil /> },
                  { path: 'leads', element: <ChatbotInsightsLeads /> },
                ],
              },
              { path: 'integrations', element: <ChatbotIntegrations /> },
              {
                path: 'integrations/whatsapp',
                element: <WhatsappIntegration />,
              },
              // {
              //   path: 'integrations/intercom',
              //   element: <IntercomIntegration />,
              // },
              {
                path: 'integrations/wordpress',
                element: <WordPressIntegration />,
              },
              { path: 'safety', element: <ChatbotSafety /> },
              { path: 'add/knowledge', element: <AddKnowledgeFile /> },
              { path: 'knowledge', element: <ChatbotKnowledge /> },
              { path: 'personality', element: <Behavior /> },
              { path: 'personality/add', element: <AddPersonality /> },
              { path: 'personality/:personalityId/edit', element: <AddPersonality /> },
              { path: 'setup', element: <ChatbotSetup /> },
              { path: 'styling', element: <ChatbotStyling /> },
              { path: 'powerups', element: <ChatbotPowerups /> },
              { path: 'realtime-voice', element: <ChatbotRealtimeVoice /> },
              { path: 'deployments', element: <ChatbotDeployments /> },
              { path: 'deployment/add', element: <ChatbotDeploymentAdd /> },
              { path: 'deployment/:deploymentId/share', element: <ShareAIChatbot /> },
            ],
          },
        ],
      },
      {
        path: 'profile',
        element: (
          <LayoutShell>
            <Profile />
          </LayoutShell>
        ),
      },
      {
        path: 'training/:job_id',
        element: (
          <LayoutShell>
            <TrainingPage />
          </LayoutShell>
        ),
      },
      {
        path: 'membership',
        element: (
          <LayoutShell>
            <Membership />
          </LayoutShell>
        ),
      },
      {
        path: 'billing',
        element: (
          <LayoutShell>
            <Billing />
          </LayoutShell>
        ),
        children: [
          { index: true, element: <Navigate to="subscription" replace /> },
          { path: 'subscription', element: <BillingSubscription /> },
          { path: 'add-ons', element: <BillingAddOns /> },
          { path: 'invoices', element: <BillingInvoices /> },
          { path: 'payment', element: <BillingPayment /> },
        ],
      },
      {
        path: 'membership-page',
        element: (
          <LayoutShell>
            <MembershipPage />
          </LayoutShell>
        ),
      },
      {
        path: 'team-management',
        element: (
          <LayoutShell>
            <TeamManagement />
          </LayoutShell>
        ),
        children: [{ path: ':id', element: <TeamManagement /> }],
      },
      {
        path: 'voice',
        element: (
          <LayoutShell>
            <Outlet />
          </LayoutShell>
        ),
        children: [
          { index: true, element: <Voice /> },
          { path: 'create', element: <CreateVoice /> },
          {
            path: ':id',
            element: <VoiceDetailsOutlet />,
            children: [
              { path: 'conversations', element: <VoiceConversations /> },
              { path: 'analytics', element: <VoiceInsights /> },
              { path: 'conversations/:conversationId', element: <VoiceConversationDetails /> },
              { path: 'edit', element: <EditVoice /> },
            ]
          },
        ],
      },
      {
        path: 'phone-numbers',
        element: (
          <LayoutShell>
            <Outlet />
          </LayoutShell>
        ),
        children: [
          { index: true, element: <PhoneNumbers /> },
          { path: 'purchase', element: <PhoneNumberPurchase /> },
          { path: 'purchase/success', element: <PhoneNumberPurchaseSuccess /> },
          { path: 'purchase/cancel', element: <PhoneNumberPurchaseCancel /> },
        ],
      },
      {
        path: 'human-handover',
        element: (
          <LayoutShell>
            <HumanHandover />
          </LayoutShell>
        ),
      },
       {
        path: 'human-handover/add-agent',
        element: (
          <LayoutShell>
            <AddAgent />
          </LayoutShell>
        ),
      },
      {
        path: 'human-handover/edit-agent',
        element: (
          <LayoutShell>
            <EditAgent />
          </LayoutShell>
        ),
      },
      {
        path: 'human-handover/:organization_id',
        element: (
          <LayoutLiveAgent>
            <LiveAgentEntrypoint />
          </LayoutLiveAgent>
        ),
        children: [
          { index: true, element: <HumanHandoverDashboard /> },
          { path: 'insights', element: <HumanHandoverInsights /> },
          { path: 'quick-responses', element: <HumanHandoverQuickResponses /> },
          { path: 'settings', element: <HumanHandoverSettings /> },
          { path: 'conversation/:conversation_id', element: <HumanHandoverConversation /> },
        ],
      },
      {
        path: 'subscribe',
        element: (
          <LayoutShell>
            <Subscribe />
          </LayoutShell>
        ),
      },
      {
        path: 'plans',
        element: (
          <LayoutShell>
            <Plans />
          </LayoutShell>
        ),
      },
      {
        path: 'admin/dashboard',
        element: (
          <LayoutShell>
            <AdminDashboard />
          </LayoutShell>
        ),
      },
      {
        path: 'accept-team-invite',
        element: <AcceptTeamInvite />,
      },
      {
        path: 'change-password',
        element: (
          <PrivateRoute>
            <Outlet />
          </PrivateRoute>
        ),
        children: [{ path: '', element: <ChangePasswordPage /> }],
      },
    ],
  },
  {
    path: '/embed/preview',
    element: (
      <TokenCheckRoute>
        <BubblePreviewPage />
      </TokenCheckRoute>
    ),
  },
  // Standalone AIVoiceDemo route - protected only by its own token system
  {
    path: '/ai-voice/demo',
    element: (
      <>
        <PageViewTracker />
        <RouteChangeHandler />
        <AIVoiceDemo />
      </>
    )
  },
  {
    path: '/stripe',
    element: (
      <PrivateRoute>
        <Outlet />
      </PrivateRoute>
    ),
    children: [
      {
        path: 'success',
        element: (
          <>
            <PageViewTracker />
            <RouteChangeHandler />
            <Success />
          </>
        )
      },
      {
        path: 'cancel',
        element: (
          <>
            <PageViewTracker />
            <RouteChangeHandler />
            <Cancel />
          </>
        )
      },
    ],
  },
      {
        path: '/tiger-group/demo',
        element: (
          <>
            <ArabicDemo />
          </>
        ),
      },
      {
        path: '*',
        element: <ErrorPage />,
      },
    ],
  },
]);

export default function Routers() {
  return (
    <Suspense fallback={<DLoading show={true} />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
